/**
 * CSV serialisation + download, for report exports.
 *
 * Values are emitted per RFC 4180: fields are quoted when they contain a
 * delimiter, quote or newline, and embedded quotes are doubled.
 */

export type CsvValue = string | number | null | undefined;

/**
 * Excel and Sheets treat a leading `=`, `+`, `-`, `@`, tab or CR in a cell as
 * the start of a formula, so a value like `=cmd|...` becomes executable when
 * the file is opened. Names and labels here come from NOS, which we don't
 * control, so text fields get a leading apostrophe to neutralise it.
 *
 * Numbers are never guarded — they are written as numbers, so a negative one
 * must keep its sign.
 */
const FORMULA_START = /^[=+\-@\t\r]/;

function escapeField(value: CsvValue): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";

  const guarded = FORMULA_START.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replaceAll('"', '""')}"` : guarded;
}

/**
 * Rows (the first is conventionally the header) as a CSV document. CRLF line
 * endings, per RFC 4180 and what Excel expects.
 */
export function toCsv(rows: CsvValue[][]): string {
  return rows.map((row) => row.map(escapeField).join(",")).join("\r\n");
}

/**
 * Hands the browser a CSV file to save.
 *
 * The content is prefixed with a UTF-8 BOM: without it Excel on Windows reads
 * the file as the local codepage and mangles any non-ASCII name.
 */
export function downloadCsv(filename: string, rows: CsvValue[][]): void {
  const blob = new Blob([`﻿${toCsv(rows)}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in some browsers; a tick is
  // enough for the navigation to have started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * A CSV file as an HTTP download: header row, then one row per record.
 *
 * Server-side exports go through this rather than calling `toCsv` themselves.
 * `toCsv` takes a single rows array, so building the document by hand invites
 * `toCsv(headers, body)` — which throws `row.map is not a function` at request
 * time, because a flat `string[]` of headers has no per-row `.map`. Both the
 * audit-log and person-directory exports shipped that way and 500'd: `tsc`
 * flagged the arity, but it isn't in the pre-commit gate, so nothing stopped
 * it. One helper, one shape, one place to get it right.
 */
export function csvResponse(filename: string, headers: string[], body: CsvValue[][]): Response {
  return new Response(toCsv([headers, ...body]), {
    headers: {
      "Content-Type": "text/csv; charset=utf-8",
      "Content-Disposition": `attachment; filename="${filename}"`,
    },
  });
}
